/*  MUI  */
import { Button, Typography } from '@mui/material';

/*  Shared  */
import { Image } from 'application/Shared/Components';
import { Route, Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';

/*  Objects  */
import {
    Contract,
    ContractDate,
    ContractMRR,
    ContractNRC,
    ContractNotes,
    ContractTCV,
    ContractType,
    ContractUsers
} from 'objects/Contract';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const ModalTitle = ({ name, favico }) => (
    <>
        <Image
            avatar
            imageId={favico}
            tooltipText={name}
            style={{
                marginRight: '7px'
            }}
        />
        {name}
    </>
);

export const AppContractDetailsModal = ({
    abbreviatedMonthDate,
    closeModal,
    currentContract,
    isForReport,
    viewingContract
}) => {
    const { t } = useTranslation();
    const { name, favico } = currentContract.application;
    const { clientId, partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({ clientId, partnerId });

    return (
        <Modal
            data-testid="appContractDetailsModal"
            open={viewingContract}
            style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center'
            }}
            paperProps={{ minWidth: '500px' }}
            onClose={() => closeModal()}
            maxWidth="xl"
            fullWidth
            role="dialog"
            scroll="paper"
            aria-labelledby="contract-details"
            TransitionComponent={Transition}
            title={<ModalTitle name={name} favico={favico} />}
            titleProps={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center'
            }}
        >
            <>
                <ModalBody dividers>
                    <Contract data={currentContract}>
                        <Typography
                            color="textSecondary"
                            variant="h4"
                            style={{ fontWeight: 600, marginBottom: '12px' }}
                        >
                            {t('ApplicationContractManagement.contractDetails')}
                        </Typography>
                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{ fontWeight: 600 }}
                        >
                            {t('ApplicationContractManagement.contractType')}
                        </Typography>
                        <ContractType />

                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{
                                fontWeight: 600,
                                marginTop: '12px'
                            }}
                        >
                            {t('ApplicationContractManagement.renewalDate')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractDate
                                abbreviatedMonthDate={abbreviatedMonthDate}
                            />
                        </Typography>

                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{
                                fontWeight: 600,
                                marginTop: '12px'
                            }}
                        >
                            {t('ApplicationContractManagement.users')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractUsers isForReport={isForReport} />
                        </Typography>
                        <hr />

                        <Typography
                            variant="h4"
                            style={{ fontWeight: 600, marginBottom: '12px' }}
                        >
                            {t(
                                'ApplicationContractManagement.contractFinancials'
                            )}
                        </Typography>
                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{
                                fontWeight: 600,
                                marginTop: '12px'
                            }}
                        >
                            {t('ApplicationContractManagement.nrc')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractNRC />
                        </Typography>

                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{
                                fontWeight: 600,
                                marginTop: '12px'
                            }}
                        >
                            {t('ApplicationContractManagement.mrr')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractMRR />
                        </Typography>

                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{
                                fontWeight: 600,
                                marginTop: '12px'
                            }}
                        >
                            {t('ApplicationContractManagement.tcv')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractTCV />
                        </Typography>
                        <hr />

                        <Typography
                            variant="h4"
                            style={{ fontWeight: 600, marginBottom: '12px' }}
                        >
                            {t('ApplicationContractManagement.contractNotes')}
                        </Typography>
                        <Typography variant="body2">
                            <ContractNotes />
                        </Typography>
                    </Contract>
                </ModalBody>
                <ModalFooter>
                    <Button
                        data-testid="closeModalBtn"
                        data-trackid="admin_contracts_contractDetails_close"
                        onClick={() => closeModal()}
                        variant="outlined"
                        color="primary"
                        style={{ marginRight: '8px' }}
                    >
                        {t('Common.close')}
                    </Button>
                    <Link
                        data-trackid="admin_contracts_contractDetails_routeTo_view"
                        to={`${baseAdminRoute}/application/${currentContract.application.id}/contract`}
                        state={{
                            from: isForReport
                                ? t('Reports.Title.contracts.report')
                                : t('ContractManagement.calendar')
                        }}
                    >
                        <Button
                            data-testid="viewAppBtn"
                            data-trackid="admin_contracts_contractDetails_view"
                            variant="contained"
                            onClick={() => closeModal()}
                            color="primary"
                        >
                            {t('Common.view')}
                        </Button>
                    </Link>
                </ModalFooter>
            </>
        </Modal>
    );
};
